<template>
  <div class="mcl-page relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">

    <!-- Toggle Filter Button (top-left corner) -->
    <button type="button" @click="toggleFilter"
      class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
      <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
      <span v-if="activeFilterCount > 0" class="mcl-filter-toggle-badge">{{ activeFilterCount }}</span>
    </button>

    <transition name="slide">
      <aside v-show="showFilter"
        class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r border-gray-200">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
            <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
            Filters
          </h2>
        </div>
        <div class="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-2">Entity</label>
            <multiselect v-model="filterByEntities" :options="entityOptions" :multiple="true" :close-on-select="false"
              :preserve-search="true" placeholder="Select entities..." />
            <p v-if="filterByEntities.length > 0" class="mcl-filter-hint">{{ filterByEntities.length }} selected</p>
          </div>
          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-2">Partner type</label>
            <multiselect :model-value="singleSelectDisplay(partnerTypeFilterOptions, filterPartnerType)"
              @update:modelValue="onPartnerTypeFilterInput" :options="partnerTypeFilterOptions" :multiple="false"
              :close-on-select="true" :preserve-search="true" label="label" track-by="value" placeholder="Select..." />
          </div>

          <div class="flex flex-col">
            <label class="block text-sm font-medium text-gray-700 mb-2">Partners</label>
            <multiselect v-model="filterByPartners" :options="partnersForFilterOptions" :multiple="true"
              :close-on-select="false" :preserve-search="true" label="name" track-by="id"
              :disabled="partnersForFilterOptions.length === 0" placeholder="Select partners...">
              <template #option="props">
                <span class="option__desc">
                  <span class="option__title">{{ props.option.name }}</span>
                  <span class="option__small"> ({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                </span>
              </template>
            </multiselect>
            <p v-if="filterByPartners.length > 0" class="mcl-filter-hint">{{ filterByPartners.length }} selected</p>
            <p v-else-if="filterPartnerType !== 'ALL'" class="mcl-filter-hint">
              Showing {{ filterPartnerType === 'CURRENT' ? 'current' : 'pipeline' }} partners only
            </p>
          </div>
        </div>

        <div class="px-6 pt-5 pb-6 border-t border-gray-200 bg-gray-50">
          <div class="flex gap-3">
            <button type="button"
              class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 transition-colors duration-150"
              @click.stop="clearFilters">
              <font-awesome-icon :icon="['fas', 'undo']" class="text-gray-500 text-sm" />
              Clear
            </button>
            <button type="button"
              class="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 border border-blue-600 shadow-md hover:bg-blue-700 hover:border-blue-700 hover:shadow-lg transition-all duration-150"
              @click.stop="applyFilters">
              <font-awesome-icon :icon="['fas', 'check']" class="text-sm opacity-90" />
              Apply
            </button>
          </div>
        </div>
      </aside>
    </transition>

    <div class="h-full container mx-auto">
      <div class="flex flex-col h-full w-full p-0 sm:p-8">
        <nav class="text-sm text-gray-600 mb-6 flex flex-wrap items-center">
          <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
          <span class="mx-1">/</span>
          <router-link to="/partnerships" class="hover:text-blue-700 transition font-medium">Partnerships</router-link>
          <span class="mx-1">/</span>
          <span class="text-blue-900 font-semibold">Master Contact List</span>
        </nav>

        <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-100">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 class="text-2xl font-bold text-gray-900 mb-2">Master Contact List</h1>
              <p class="text-gray-600">Central directory for partnership contacts.</p>
            </div>
            <div class="flex items-center gap-2 flex-wrap">
              <button type="button" class="mcl-email-logs-btn" @click.stop="openEmailLogs">
                <font-awesome-icon :icon="['fas', 'list-alt']" class="mcl-email-logs-btn__icon" />
                <span>Email Logs</span>
              </button>
              <button type="button" class="mcl-email-btn" @click.stop="openEmailCompose">
                <font-awesome-icon :icon="['fas', 'envelope']" class="mcl-email-btn__icon" />
                <span>Send Email</span>
              </button>
              <button type="button" class="mcl-add-btn" @click.stop="openCreateModal">
                <icon-plus class="mcl-add-btn__icon"></icon-plus>
                <span>Add Contact</span>
              </button>
            </div>
          </div>
        </div>

        <section class="mcl-stats mb-6" aria-label="Contact statistics">
          <div class="mcl-stats-section">
            <div class="mcl-stats-section__head">
              <h2 class="mcl-stats-section__title">Overview</h2>
              <p class="mcl-stats-section__subtitle">Counts reflect your current search and filters</p>
            </div>
            <div class="mcl-stats-overview">
              <article class="mcl-stat-card mcl-stat-card--total">
                <div class="mcl-stat-card__glow mcl-stat-card__glow--blue" aria-hidden="true"></div>
                <div class="mcl-stat-card__body">
                  <div class="mcl-stat-card__icon-wrap mcl-stat-card__icon-wrap--blue">
                    <font-awesome-icon :icon="['fas', 'users']" />
                  </div>
                  <div class="mcl-stat-card__content">
                    <p class="mcl-stat-card__label">Total contacts</p>
                    <p v-if="isStatsLoading" class="mcl-stat-card__value mcl-stat-card__value--loading"></p>
                    <p v-else class="mcl-stat-card__value">{{ formatStatCount(statsTotalContacts) }}</p>
                  </div>
                </div>
              </article>
              <article v-for="card in partnerTypeStatCards" :key="card.key" class="mcl-stat-card"
                :class="'mcl-stat-card--' + card.key">
                <div class="mcl-stat-card__glow" :class="'mcl-stat-card__glow--' + card.key" aria-hidden="true"></div>
                <div class="mcl-stat-card__body">
                  <div class="mcl-stat-card__icon-wrap" :class="'mcl-stat-card__icon-wrap--' + card.key">
                    <font-awesome-icon :icon="['fas', card.icon]" />
                  </div>
                  <div class="mcl-stat-card__content">
                    <p class="mcl-stat-card__label">{{ card.label }}</p>
                    <p v-if="isStatsLoading" class="mcl-stat-card__value mcl-stat-card__value--loading"></p>
                    <p v-else class="mcl-stat-card__value">{{ formatStatCount(card.count) }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
          <div class="mcl-stats-section">
            <div class="mcl-stats-section__head">
              <h2 class="mcl-stats-section__title">By entity</h2>
              <p class="mcl-stats-section__subtitle">
                Partner contacts count toward each entity on their partner record; standalone contacts use their
                assigned
                entity
              </p>
            </div>
            <div class="mcl-stats-entities">
              <article v-for="card in entityStatCards" :key="card.entity" class="mcl-stat-card mcl-stat-card--entity"
                :class="'mcl-stat-card--entity-' + card.entity.toLowerCase()">
                <div class="mcl-stat-card__body mcl-stat-card__body--entity">
                  <div class="mcl-stat-card__entity-badge">{{ card.entity }}</div>
                  <p v-if="isStatsLoading"
                    class="mcl-stat-card__value mcl-stat-card__value--loading mcl-stat-card__value--sm"></p>
                  <p v-else class="mcl-stat-card__value mcl-stat-card__value--sm">{{ formatStatCount(card.count) }}</p>
                  <p class="mcl-stat-card__entity-caption">contacts</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <div class="bg-white shadow-sm rounded-lg flex-auto flex flex-col border border-gray-100">
          <div class="p-6 border-b border-gray-200">
            <div class="flex items-center justify-between gap-3 flex-wrap">
              <h2 class="text-lg font-semibold text-gray-900">Contact records</h2>
              <div class="flex items-center gap-3 flex-wrap w-full sm:w-auto sm:ml-auto">
                <div class="relative w-full sm:w-72">
                  <input v-model="search" type="text" placeholder="Search contacts or partners..."
                    class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    @keyup.enter="onSearch" />
                  <font-awesome-icon :icon="['fas', 'search']"
                    class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                </div>
                <button type="button" class="mcl-export-btn" :disabled="isExportExcel" @click.stop="generateReport">
                  <font-awesome-icon :icon="['fas', 'file-excel']" class="mcl-export-btn__icon" />
                  <span>{{ isExportExcel ? 'Exporting Excel' : 'Export Excel' }}</span>
                </button>
              </div>
            </div>
          </div>

          <div v-if="hasActiveFilters"
            class="px-6 py-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex flex-wrap items-center gap-2">
            <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">Active filters</span>
            <span v-if="appliedFilterEntities.length" class="mcl-active-filter-chip">
              Entity: {{ appliedFilterEntities.join(', ') }}
            </span>
            <span v-if="appliedPartnerTypeLabel" class="mcl-active-filter-chip">
              Partner type: {{ appliedPartnerTypeLabel }}
            </span>
            <span v-if="appliedFilterPartners.length" class="mcl-active-filter-chip">
              Partners: {{ appliedFilterPartners.length }} selected
            </span>
            <button type="button" class="mcl-active-filter-clear" @click="clearFilters">
              Clear all
            </button>
          </div>

          <div v-if="isLoading" class="flex-auto flex justify-center items-center py-12">
            <div class="text-center max-w-md px-4">
              <div class="text-gray-700 font-medium mb-1">Loading…</div>
              <div class="text-gray-500 text-sm">Fetching contact records.</div>
            </div>
          </div>

          <div v-else-if="contactRecords && contactRecords.length === 0"
            class="flex-auto flex justify-center items-center py-12">
            <div class="text-center max-w-md px-4">
              <h2 class="text-xl font-semibold text-gray-800 mb-2">No contacts found</h2>
              <p class="text-gray-500 mb-4">
                {{ hasActiveFilters || search ? 'Try adjusting your search or filters.' :
                  'No contact persons have been added yet.' }}
              </p>
              <button v-if="hasActiveFilters || search" type="button"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                @click="toggleFilter">
                <font-awesome-icon :icon="['fas', 'sliders-h']" />
                Open filters
              </button>
            </div>
          </div>

          <div v-else class="flex flex-col flex-auto">
            <div class="flex-auto flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Name</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Phone</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Email</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Designation</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Organization Name</th>
                        <th scope="col" class="px-6 py-2 text-gray-400">Entity</th>
                        <th scope="col" class="px-6 py-2 text-gray-400 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in contactRecords" :key="item.id"
                        class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50">
                        <td class="whitespace-nowrap px-6 py-2 font-medium text-gray-900">
                          {{ rowNumber(index) }}
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ displayValue(item.name) }}
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ displayValue(item.phone) }}
                        </td>
                        <td class="px-6 py-2 text-gray-900">
                          <span v-if="item.email" class="text-blue-700">{{ item.email }}</span>
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ displayValue(item.designation) }}
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ partnerName(item) }}
                        </td>
                        <td class="px-6 py-2 text-gray-900 whitespace-nowrap">
                          {{ contactEntity(item) }}
                        </td>
                        <td class="px-6 py-2">
                          <div class="flex justify-end gap-2">
                            <button type="button" title="Edit"
                              class="bg-gray-100 hover:bg-indigo-50 w-8 h-8 flex justify-center items-center rounded-lg"
                              @click.stop="openEditModal(item)">
                              <icon-pen class="w-4 h-4"></icon-pen>
                            </button>
                            <button type="button" title="Delete"
                              class="bg-gray-100 hover:bg-red-50 w-8 h-8 flex justify-center items-center rounded-lg"
                              @click.stop="deleteContact(item)">
                              <icon-trash class="w-4 h-4"></icon-trash>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="mt-4 px-6 pb-6" v-if="paginatorInfo && paginatorInfo.total > 0">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>
        </div>

        <transition name="fade">
          <base-modal v-if="formModal" width="560px" content-overflow="visible" @close="closeFormModal">
            <template #header>
              <div class="mcl-modal-header">
                <div class="mcl-modal-header-inner">
                  <div class="mcl-modal-header-icon-wrap">
                    <font-awesome-icon :icon="isEditMode ? ['fas', 'user-pen'] : ['fas', 'user-plus']"
                      class="mcl-modal-header-icon" />
                  </div>
                  <div class="text-left">
                    <h2 class="mcl-modal-header-title">{{ isEditMode ? 'Edit Contact' : 'Add New Contact' }}</h2>
                    <p class="mcl-modal-header-sub">
                      {{ isEditMode ? 'Update contact details in the master directory' :
                        'Add a person to the master contact directory' }}
                    </p>
                  </div>
                </div>
              </div>
            </template>

            <div class="mcl-modal-body">
              <div class="mcl-modal-card">
                <div class="mcl-modal-grid">
                  <div class="mcl-field">
                    <label class="mcl-label">
                      Name <span class="mcl-required">*</span>
                    </label>
                    <input v-model.trim="createForm.name" type="text" class="mcl-input"
                      :class="{ 'mcl-input-error': formErrors.name }" placeholder="Full name"
                      @input="clearFieldError('name')" />
                    <div class="mcl-error-slot" :class="{ 'mcl-error-slot--visible': formErrors.name }">
                      <transition name="mcl-error-fade">
                        <p v-if="formErrors.name" key="name" class="mcl-error">{{ formErrors.name }}</p>
                      </transition>
                    </div>
                  </div>

                  <div class="mcl-field">
                    <label class="mcl-label">
                      Phone <span class="mcl-required">*</span>
                    </label>
                    <input v-model.trim="createForm.phone" type="text" class="mcl-input"
                      :class="{ 'mcl-input-error': formErrors.phone }" placeholder="Phone number"
                      @keypress="allowNumbersOnly" @input="clearFieldError('phone')" />
                    <div class="mcl-error-slot" :class="{ 'mcl-error-slot--visible': formErrors.phone }">
                      <transition name="mcl-error-fade">
                        <p v-if="formErrors.phone" key="phone" class="mcl-error">{{ formErrors.phone }}</p>
                      </transition>
                    </div>
                  </div>

                  <div class="mcl-field">
                    <label class="mcl-label">
                      Email <span class="mcl-required">*</span>
                    </label>
                    <input v-model.trim="createForm.email" type="email" class="mcl-input"
                      :class="{ 'mcl-input-error': formErrors.email }" placeholder="email@example.com"
                      @input="clearFieldError('email')" />
                    <div class="mcl-error-slot" :class="{ 'mcl-error-slot--visible': formErrors.email }">
                      <transition name="mcl-error-fade">
                        <p v-if="formErrors.email" key="email" class="mcl-error">{{ formErrors.email }}</p>
                      </transition>
                    </div>
                  </div>

                  <div class="mcl-field">
                    <label class="mcl-label">Designation</label>
                    <input v-model.trim="createForm.designation" type="text" class="mcl-input"
                      placeholder="Job title" />
                  </div>
                </div>
              </div>

              <div class="mcl-modal-card mcl-modal-card-partner">
                <label class="mcl-label mcl-label-block">
                  Does this person belong to a current or pipeline partner?
                </label>
                <select v-model="belongsToPartner" class="mcl-input mcl-select" @change="onBelongsToPartnerChange">
                  <option value="no">No — standalone contact</option>
                  <option value="yes">Yes — link to a partner</option>
                </select>

                <div v-if="belongsToPartner === 'no'" class="mcl-partner-select-wrap">
                  <label class="mcl-label mcl-label-block">
                    Entity <span class="mcl-required">*</span>
                  </label>
                  <select v-model="createForm.entity" class="mcl-input mcl-select"
                    :class="{ 'mcl-input-error': formErrors.entity }" @change="clearFieldError('entity')">
                    <option value="">Select entity</option>
                    <option v-for="entityOption in entityOptions" :key="entityOption" :value="entityOption">
                      {{ entityOption }}
                    </option>
                  </select>
                  <div class="mcl-error-slot" :class="{ 'mcl-error-slot--visible': formErrors.entity }">
                    <transition name="mcl-error-fade">
                      <p v-if="formErrors.entity" key="entity" class="mcl-error">{{ formErrors.entity }}</p>
                    </transition>
                  </div>
                </div>

                <div v-if="belongsToPartner === 'yes'" class="mcl-partner-select-wrap">
                  <label class="mcl-label mcl-label-block">
                    Select Partner <span class="mcl-required">*</span>
                  </label>
                  <multiselect v-model="selectedPartner" :options="partnersForSelect" :multiple="false"
                    :close-on-select="true" :preserve-search="true" placeholder="Search current or pipeline partner…"
                    label="name" track-by="id" class="mcl-multiselect" @select="clearFieldError('current_partner_id')">
                    <template #singleLabel="props">
                      <span class="option__desc">
                        <span class="option__title">{{ props.option.name }}</span>
                        <span class="option__small"> ({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                      </span>
                    </template>
                    <template #option="props">
                      <span class="option__desc">
                        <span class="option__title">{{ props.option.name }}</span>
                        <span class="option__small"> ({{ props.option.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                      </span>
                    </template>
                  </multiselect>
                  <div class="mcl-error-slot" :class="{ 'mcl-error-slot--visible': formErrors.current_partner_id }">
                    <transition name="mcl-error-fade">
                      <p v-if="formErrors.current_partner_id" key="partner" class="mcl-error">
                        {{ formErrors.current_partner_id }}
                      </p>
                    </transition>
                  </div>
                </div>
              </div>

              <div class="mcl-modal-actions">
                <button type="button" class="mcl-modal-btn mcl-modal-btn-secondary" :disabled="isSubmitting"
                  @click="closeFormModal">
                  Cancel
                </button>
                <button type="button" class="mcl-modal-btn mcl-modal-btn-primary" :disabled="isSubmitting"
                  @click="submitForm">
                  <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" spin class="mr-2" />
                  <font-awesome-icon v-else :icon="['fas', 'check']" class="mr-2" />
                  {{ isSubmitting ? 'Saving…' : (isEditMode ? 'Update Contact' : 'Save Contact') }}
                </button>
              </div>

              <p class="mcl-modal-hint">
                <font-awesome-icon :icon="['fas', 'lightbulb']" class="mcl-modal-hint-icon" />
                Required fields: name, phone, and email. Standalone contacts require an entity; linked contacts use the
                partner entity.
              </p>
            </div>
          </base-modal>
        </transition>
      </div>
    </div>

    <GmailCompose :visible="showEmailCompose" :compose-draft="composeDraft" :list-filters="composeContactFilters"
      :apply-list-filters="!composeIgnoreListFilters" @close="closeEmailCompose" @sent="onComposeSent" />

    <transition name="fade">
      <base-full-modal v-if="showEmailLogsModal" title="Email Logs" @close="closeEmailLogs">
        <email-logs ref="emailLogsRef" class="text-left" @resend="onResendEmail"></email-logs>
      </base-full-modal>
    </transition>
  </div>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import Pagination from "../../components/shared/pagination.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import GmailCompose from "./components/GmailCompose.vue";
import EmailLogs from "./components/EmailLogs.vue";

export default {
  name: "master-contact-list-index",
  components: {
    Pagination,
    Multiselect,
    IconPlus,
    IconPen,
    IconTrash,
    GmailCompose,
    EmailLogs,
  },
  data() {
    return {
      isLoading: false,
      isStatsLoading: false,
      contactStats: null,
      isExportExcel: false,
      isApiCallRecurisive: false,
      isSubmitting: false,
      formModal: false,
      modalMode: "create",
      editingContactId: null,
      showFilter: false,
      showEmailCompose: false,
      showEmailLogsModal: false,
      composeDraft: null,
      composeIgnoreListFilters: false,
      resendFromFolder: null,
      filterByEntities: [],
      filterPartnerType: "ALL",
      filterByPartners: [],
      appliedFilterEntities: [],
      appliedPartnerType: "ALL",
      appliedFilterPartners: [],
      allPartnersForSelect: [],
      belongsToPartner: "no",
      selectedPartner: null,
      partnersForSelect: [],
      entityOptions: ["IAT", "ATS", "FCHS", "ADPOLY", "ADVETI"],
      partnerTypeFilterOptions: [
        { label: "All partners", value: "ALL" },
        { label: "Current partners", value: "CURRENT" },
        { label: "Pipeline partners", value: "PIPELINE" },
      ],
      generatedReport: [],
      contactRecords: [],
      paginatorInfo: null,
      first: 10,
      page: 1,
      search: "",
      searchDebounceTimer: null,
      createForm: {
        name: "",
        phone: "",
        email: "",
        designation: "",
        entity: "",
      },
      formErrors: {
        name: "",
        phone: "",
        email: "",
        current_partner_id: "",
        entity: "",
      },
    };
  },
  mounted() {
    this.fetchAllPartnersForSelect();
    this.refreshContactList();
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  computed: {
    composeContactFilters() {
      var entities = null;
      if (this.appliedFilterEntities && this.appliedFilterEntities.length > 0) {
        entities = this.appliedFilterEntities.slice();
      }

      var partnerType = null;
      if (this.appliedPartnerType && this.appliedPartnerType !== "ALL") {
        partnerType = this.appliedPartnerType;
      }

      var partnerIds = null;
      if (this.appliedFilterPartners && this.appliedFilterPartners.length > 0) {
        partnerIds = [];
        for (var i = 0; i < this.appliedFilterPartners.length; i++) {
          partnerIds.push(parseInt(this.appliedFilterPartners[i].id, 10));
        }
      }

      return {
        entities: entities,
        partner_type: partnerType,
        partner_ids: partnerIds,
      };
    },
    isEditMode() {
      return this.modalMode === "edit";
    },
    partnersForFilterOptions() {
      var list = this.allPartnersForSelect || [];
      if (this.filterPartnerType === "CURRENT") {
        return list.filter(function (partner) {
          return !partner.is_pipeline;
        });
      }
      if (this.filterPartnerType === "PIPELINE") {
        return list.filter(function (partner) {
          return partner.is_pipeline;
        });
      }
      return list;
    },
    activeFilterCount() {
      var count = 0;
      if (this.appliedFilterEntities && this.appliedFilterEntities.length > 0) {
        count = count + 1;
      }
      if (this.appliedPartnerType && this.appliedPartnerType !== "ALL") {
        count = count + 1;
      }
      if (this.appliedFilterPartners && this.appliedFilterPartners.length > 0) {
        count = count + 1;
      }
      return count;
    },
    hasActiveFilters() {
      return this.activeFilterCount > 0;
    },
    appliedPartnerTypeLabel() {
      if (this.appliedPartnerType === "CURRENT") {
        return "Current partners";
      }
      if (this.appliedPartnerType === "PIPELINE") {
        return "Pipeline partners";
      }
      return null;
    },
    statsTotalContacts() {
      if (!this.contactStats) {
        return 0;
      }
      return this.contactStats.total_contacts || 0;
    },
    partnerTypeStatCards() {
      var stats = this.contactStats || {};
      return [
        {
          key: "current",
          label: "Current partners",
          count: stats.current_partner_contacts || 0,
          icon: "building",
        },
        {
          key: "pipeline",
          label: "Pipeline partners",
          count: stats.pipeline_partner_contacts || 0,
          icon: "handshake",
        },
        {
          key: "other",
          label: "Other (standalone)",
          count: stats.other_contacts || 0,
          icon: "user",
        },
      ];
    },
    entityStatCards() {
      var countMap = {};
      var entityCounts =
        this.contactStats && this.contactStats.entity_counts
          ? this.contactStats.entity_counts
          : [];
      var i;
      for (i = 0; i < entityCounts.length; i++) {
        countMap[entityCounts[i].entity] = entityCounts[i].count;
      }
      var cards = [];
      for (i = 0; i < this.entityOptions.length; i++) {
        var entity = this.entityOptions[i];
        cards.push({
          entity: entity,
          count: countMap[entity] || 0,
        });
      }
      return cards;
    },
  },
  watch: {
    search() {
      this.onSearchInput();
    },
    filterPartnerType() {
      var allowedIds = {};
      var options = this.partnersForFilterOptions;
      for (var i = 0; i < options.length; i++) {
        allowedIds[options[i].id] = true;
      }
      this.filterByPartners = this.filterByPartners.filter(function (partner) {
        return allowedIds[partner.id];
      });
    },
  },
  methods: {
    singleSelectDisplay(options, primitiveValue) {
      if (!options || !options.length) {
        return null;
      }
      var v = primitiveValue;
      if (v === undefined || v === null) {
        v = "";
      }
      var i;
      for (i = 0; i < options.length; i++) {
        if (options[i].value === v) {
          return options[i];
        }
      }
      return null;
    },
    onPartnerTypeFilterInput(option) {
      if (!option) {
        this.filterPartnerType = "ALL";
        return;
      }
      if (typeof option === "object" && option.value !== undefined) {
        this.filterPartnerType = option.value;
        return;
      }
      if (typeof option === "string") {
        this.filterPartnerType = option;
      }
    },
    toggleFilter() {
      if (!this.showFilter) {
        this.filterByEntities = this.appliedFilterEntities.slice();
        this.filterPartnerType = this.appliedPartnerType;
        this.filterByPartners = this.appliedFilterPartners.slice();
      }
      this.showFilter = !this.showFilter;
    },
    applyFilters() {
      this.appliedFilterEntities = this.filterByEntities.slice();
      this.appliedPartnerType = this.filterPartnerType;
      this.appliedFilterPartners = this.filterByPartners.slice();
      this.page = 1;
      this.showFilter = false;
      this.refreshContactList();
    },
    clearFilters() {
      this.filterByEntities = [];
      this.filterPartnerType = "ALL";
      this.filterByPartners = [];
      this.appliedFilterEntities = [];
      this.appliedPartnerType = "ALL";
      this.appliedFilterPartners = [];
      this.page = 1;
      this.refreshContactList();
    },
    buildListFilterVariables() {
      var entities = null;
      if (this.appliedFilterEntities && this.appliedFilterEntities.length > 0) {
        entities = this.appliedFilterEntities.slice();
      }

      var partnerType = null;
      if (this.appliedPartnerType && this.appliedPartnerType !== "ALL") {
        partnerType = this.appliedPartnerType;
      }

      var partnerIds = null;
      if (this.appliedFilterPartners && this.appliedFilterPartners.length > 0) {
        partnerIds = [];
        for (var i = 0; i < this.appliedFilterPartners.length; i++) {
          partnerIds.push(parseInt(this.appliedFilterPartners[i].id, 10));
        }
      }

      return {
        search: this.search ? this.search.trim() : null,
        entities: entities,
        partner_type: partnerType,
        partner_ids: partnerIds,
      };
    },
    async fetchAllPartnersForSelect() {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GetAllPartnersForSelect {
              getAllPartnersForSelect {
                error
                message
                data {
                  id
                  name
                  token
                  is_pipeline
                }
              }
            }
          `,
          fetchPolicy: "network-only",
        });

        if (
          data &&
          data.getAllPartnersForSelect &&
          !data.getAllPartnersForSelect.error &&
          data.getAllPartnersForSelect.data
        ) {
          this.allPartnersForSelect = data.getAllPartnersForSelect.data;
        } else {
          this.allPartnersForSelect = [];
        }
      } catch (e) {
        console.error("Failed to fetch partners for filters:", e);
        this.allPartnersForSelect = [];
      }
    },
    formatStatCount(value) {
      var n = parseInt(value, 10);
      if (isNaN(n)) {
        return "0";
      }
      return n.toLocaleString();
    },
    refreshContactList() {
      this.fetchContactRecords();
      this.fetchContactStats();
    },
    async fetchContactStats() {
      this.isStatsLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query MasterContactListStats(
              $search: String
              $entities: [String]
              $partner_type: String
              $partner_ids: [Int]
            ) {
              masterContactListStats(
                search: $search
                entities: $entities
                partner_type: $partner_type
                partner_ids: $partner_ids
              ) {
                error
                message
                data {
                  total_contacts
                  current_partner_contacts
                  pipeline_partner_contacts
                  other_contacts
                  entity_counts {
                    entity
                    count
                  }
                }
              }
            }
          `,
          variables: this.buildListFilterVariables(),
          fetchPolicy: "network-only",
        });

        const payload =
          data && data.masterContactListStats ? data.masterContactListStats : null;

        if (!payload || payload.error || !payload.data) {
          this.contactStats = null;
          return;
        }

        this.contactStats = payload.data;
      } catch (e) {
        console.error(e);
        this.contactStats = null;
      } finally {
        this.isStatsLoading = false;
      }
    },
    showToast(icon, message) {
      if (this.$swal && typeof this.$swal.mixin === "function") {
        const Toast = this.$swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3200,
          timerProgressBar: true,
        });
        Toast.fire({ icon: icon, title: message });
        return;
      }
      window.alert(message);
    },
    displayValue(value) {
      return value ? value : "—";
    },
    partnerName(item) {
      if (item && item.current_partner_id && item.current_partner && item.current_partner.name) {
        return item.current_partner.name;
      }
      return "—";
    },
    contactEntity(item) {
      if (item && item.entity) {
        return this.displayEntityLabel(item.entity);
      }
      return "—";
    },
    displayEntityLabel(code) {
      if (!code) {
        return "—";
      }
      var value = String(code);
      if (value.indexOf(",") !== -1) {
        var parts = value.split(",");
        var labels = [];
        var i;
        for (i = 0; i < parts.length; i++) {
          var part = parts[i].trim();
          if (!part) {
            continue;
          }
          var label = part === "ATHS" || part === "ATS" ? "ATS" : part;
          if (labels.indexOf(label) === -1) {
            labels.push(label);
          }
        }
        return labels.length ? labels.join(", ") : "—";
      }
      if (value === "ATHS" || value === "ATS") {
        return "ATS";
      }
      return value;
    },
    rowNumber(index) {
      if (this.paginatorInfo && this.paginatorInfo.firstItem) {
        return this.paginatorInfo.firstItem + index;
      }
      return index + 1;
    },
    allowNumbersOnly(e) {
      const keyCode = e.which || e.keyCode;
      if (keyCode > 31 && (keyCode < 48 || keyCode > 57)) {
        e.preventDefault();
      }
    },
    resetFormErrors() {
      this.formErrors = {
        name: "",
        phone: "",
        email: "",
        current_partner_id: "",
        entity: "",
      };
    },
    resetCreateForm() {
      this.createForm = {
        name: "",
        phone: "",
        email: "",
        designation: "",
        entity: "",
      };
      this.belongsToPartner = "no";
      this.selectedPartner = null;
      this.resetFormErrors();
    },
    openEmailCompose() {
      this.composeDraft = null;
      this.composeIgnoreListFilters = false;
      this.showEmailCompose = true;
    },
    closeEmailCompose() {
      this.showEmailCompose = false;
      this.composeDraft = null;
      this.composeIgnoreListFilters = false;
      this.resendFromFolder = null;
    },
    onResendEmail(draft) {
      if (!draft) {
        return;
      }
      this.composeIgnoreListFilters = true;
      this.resendFromFolder = draft.resendFromFolder ? draft.resendFromFolder : null;
      this.composeDraft = draft;
      this.showEmailCompose = true;
    },
    openEmailLogs() {
      this.showEmailLogsModal = true;
    },
    closeEmailLogs() {
      this.showEmailLogsModal = false;
    },
    onComposeSent(payload) {
      if (!payload) {
        return;
      }
      if (payload.error) {
        this.showToast("error", payload.message || "Failed to send email.");
        return;
      }
      this.showToast("success", payload.message || "Emails queued successfully.");
      if (this.showEmailLogsModal) {
        this.refreshEmailLogs(payload);
      }
    },
    refreshEmailLogs(payload) {
      var emailLogsRef = this.$refs.emailLogsRef;
      if (!emailLogsRef) {
        return;
      }
      if (payload && payload.scheduled) {
        if (emailLogsRef.activeFolder !== "scheduled" && typeof emailLogsRef.switchFolder === "function") {
          emailLogsRef.switchFolder("scheduled");
        } else if (typeof emailLogsRef.refreshLogs === "function") {
          emailLogsRef.refreshLogs();
        }
        this.resendFromFolder = null;
        return;
      }
      this.resendFromFolder = null;
      if (typeof emailLogsRef.refreshLogs === "function") {
        emailLogsRef.refreshLogs();
      } else if (typeof emailLogsRef.fetchEmailLogs === "function") {
        emailLogsRef.fetchEmailLogs();
      }
    },
    openCreateModal() {
      this.modalMode = "create";
      this.editingContactId = null;
      this.resetCreateForm();
      this.formModal = true;
      this.fetchPartnersForSelect();
    },
    openEditModal(item) {
      if (!item || !item.id) {
        return;
      }

      this.modalMode = "edit";
      this.editingContactId = item.id;
      this.resetFormErrors();

      this.createForm = {
        name: item.name || "",
        phone: item.phone || "",
        email: item.email || "",
        designation: item.designation || "",
        entity: "",
      };

      if (item.current_partner_id && item.current_partner) {
        this.belongsToPartner = "yes";
        this.selectedPartner = {
          id: item.current_partner.id,
          name: item.current_partner.name,
          is_pipeline: item.current_partner.is_pipeline,
        };
      } else {
        this.belongsToPartner = "no";
        this.selectedPartner = null;
        if (item.entity) {
          this.createForm.entity = this.displayEntityLabel(item.entity);
          if (this.createForm.entity === "—") {
            this.createForm.entity = "";
          }
        } else if (item.contact_person_entity && item.contact_person_entity.entity) {
          this.createForm.entity = this.displayEntityLabel(item.contact_person_entity.entity);
          if (this.createForm.entity === "—") {
            this.createForm.entity = "";
          }
        }
      }

      this.formModal = true;
      this.fetchPartnersForSelect();
    },
    closeFormModal() {
      this.formModal = false;
      this.modalMode = "create";
      this.editingContactId = null;
      this.resetCreateForm();
    },
    onBelongsToPartnerChange() {
      if (this.belongsToPartner !== "yes") {
        this.selectedPartner = null;
        this.formErrors.current_partner_id = "";
      } else {
        this.createForm.entity = "";
        this.formErrors.entity = "";
      }
    },
    clearFieldError(field) {
      if (this.formErrors[field]) {
        this.formErrors[field] = "";
      }
    },
    async fetchPartnersForSelect() {
      if (this.allPartnersForSelect && this.allPartnersForSelect.length > 0) {
        this.partnersForSelect = this.allPartnersForSelect;
        return;
      }
      await this.fetchAllPartnersForSelect();
      this.partnersForSelect = this.allPartnersForSelect;
    },
    buildFormInput() {
      return {
        name: this.createForm.name.trim(),
        phone: this.createForm.phone.trim(),
        email: this.createForm.email.trim(),
        designation: this.createForm.designation ? this.createForm.designation.trim() : null,
        current_partner_id:
          this.belongsToPartner === "yes" && this.selectedPartner ? parseInt(this.selectedPartner.id, 10) : null,
        entity:
          this.belongsToPartner === "no" && this.createForm.entity ? this.createForm.entity : null,
      };
    },
    submitForm() {
      if (this.isEditMode) {
        this.submitUpdate();
      } else {
        this.submitCreate();
      }
    },
    validateCreateForm() {
      this.resetFormErrors();
      var isValid = true;

      if (!this.createForm.name || !this.createForm.name.trim()) {
        this.formErrors.name = "Name is required.";
        isValid = false;
      }

      if (!this.createForm.phone || !this.createForm.phone.trim()) {
        this.formErrors.phone = "Phone is required.";
        isValid = false;
      }

      if (!this.createForm.email || !this.createForm.email.trim()) {
        this.formErrors.email = "Email is required.";
        isValid = false;
      } else {
        var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(this.createForm.email.trim())) {
          this.formErrors.email = "Please enter a valid email address.";
          isValid = false;
        }
      }

      if (this.belongsToPartner === "yes" && !this.selectedPartner) {
        this.formErrors.current_partner_id = "Please select a partner.";
        isValid = false;
      }

      if (this.belongsToPartner === "no" && !this.createForm.entity) {
        this.formErrors.entity = "Entity is required for standalone contacts.";
        isValid = false;
      }

      return isValid;
    },
    async submitCreate() {
      if (!this.validateCreateForm()) {
        return;
      }

      this.isSubmitting = true;

      var input = this.buildFormInput();

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateMasterContactPerson($input: CreateMasterContactPersonInput!) {
              createMasterContactPerson(input: $input) {
                error
                error_code
                message
              }
            }
          `,
          variables: { input: input },
        });

        const payload = data && data.createMasterContactPerson ? data.createMasterContactPerson : null;

        if (payload && payload.error) {
          this.showToast("error", payload.message || "Failed to create contact.");
          return;
        }

        this.showToast(
          "success",
          payload && payload.message ? payload.message : "Contact person created successfully."
        );

        this.closeFormModal();
        this.page = 1;
        this.refreshContactList();
      } catch (e) {
        console.error(e);
        this.showToast("error", e && e.message ? e.message : "Failed to create contact.");
      } finally {
        this.isSubmitting = false;
      }
    },
    async deleteContact(item) {
      if (!item || !item.id) {
        return;
      }

      if (!this.$swal) {
        return;
      }

      const result = await this.$swal.fire({
        icon: "warning",
        title: "Delete contact?",
        text: "This action cannot be undone.",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc2626",
      });

      if (!result || !result.isConfirmed) {
        return;
      }

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteMasterContactPerson($id: ID!) {
              deleteMasterContactPerson(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: { id: String(item.id) },
        });

        const payload = data && data.deleteMasterContactPerson ? data.deleteMasterContactPerson : null;

        if (payload && payload.error) {
          this.showToast("error", payload.message || "Failed to delete contact.");
          return;
        }

        this.showToast(
          "success",
          payload && payload.message ? payload.message : "Contact person deleted successfully."
        );

        if (this.contactRecords.length === 1 && this.page > 1) {
          this.page = this.page - 1;
        }

        this.refreshContactList();
      } catch (e) {
        console.error(e);
        this.showToast("error", e && e.message ? e.message : "Failed to delete contact.");
      }
    },
    async submitUpdate() {
      if (!this.validateCreateForm()) {
        return;
      }

      if (!this.editingContactId) {
        return;
      }

      this.isSubmitting = true;

      var input = this.buildFormInput();

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateMasterContactPerson($id: ID!, $input: UpdateMasterContactPersonInput!) {
              updateMasterContactPerson(id: $id, input: $input) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: String(this.editingContactId),
            input: input,
          },
        });

        const payload = data && data.updateMasterContactPerson ? data.updateMasterContactPerson : null;

        if (payload && payload.error) {
          this.showToast("error", payload.message || "Failed to update contact.");
          return;
        }

        this.showToast(
          "success",
          payload && payload.message ? payload.message : "Contact person updated successfully."
        );

        this.closeFormModal();
        this.refreshContactList();
      } catch (e) {
        console.error(e);
        this.showToast("error", e && e.message ? e.message : "Failed to update contact.");
      } finally {
        this.isSubmitting = false;
      }
    },
    setPage(pageNo) {
      this.page = pageNo;
      this.fetchContactRecords();
    },
    onSearch() {
      this.page = 1;
      this.refreshContactList();
    },
    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.refreshContactList();
      }, 400);
    },
    async generateReport() {
      this.generatedReport = [];
      this.isExportExcel = true;
      this.isApiCallRecurisive = true;

      var exportPage = 1;
      var exportLimit = 200;

      try {
        while (this.isApiCallRecurisive) {
          var responseData = await this.fetchForExporting(exportPage, exportLimit);

          if (responseData && responseData.error) {
            this.showToast("error", responseData.message || "Export failed.");
            break;
          }

          var rows = responseData && responseData.data ? responseData.data : [];
          if (rows.length > 0) {
            var self = this;
            this.generatedReport.push(
              ...rows.map(function (obj) {
                return {
                  Name: obj.name || "",
                  Phone: obj.phone || "",
                  Email: obj.email || "",
                  Designation: obj.designation || "",
                  "Organization Name": self.partnerName(obj) === "—" ? "" : self.partnerName(obj),
                  Entity: self.contactEntity(obj) === "—" ? "" : self.contactEntity(obj),
                };
              })
            );
            exportPage++;
          } else {
            this.isApiCallRecurisive = false;
          }
        }

        if (this.generatedReport.length > 0) {
          this.exportToExcel("Master_Contact_List");
        } else {
          this.showToast("info", "No contact records to export.");
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isExportExcel = false;
        this.isApiCallRecurisive = false;
      }
    },

    async fetchForExporting(pageNo, limitSize) {
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query ExportMasterContactPersons(
              $page: Int
              $limit: Int
              $search: String
              $entities: [String]
              $partner_type: String
              $partner_ids: [Int]
            ) {
              exportMasterContactPersons(
                page: $page
                limit: $limit
                search: $search
                entities: $entities
                partner_type: $partner_type
                partner_ids: $partner_ids
              ) {
                error
                error_code
                message
                data {
                  id
                  current_partner_id
                  name
                  phone
                  email
                  designation
                  entity
                  current_partner {
                    id
                    name
                    entity
                  }
                }
              }
            }
          `,
          variables: Object.assign(
            {
              page: pageNo,
              limit: limitSize,
            },
            this.buildListFilterVariables()
          ),
          fetchPolicy: "network-only",
        });

        return data && data.exportMasterContactPersons ? data.exportMasterContactPersons : null;
      } catch (e) {
        console.error(e);
        return { error: true, message: e && e.message ? e.message : "Export failed." };
      }
    },

    exportToExcel(reportName) {
      const worksheet = xlsx.utils.json_to_sheet(this.generatedReport);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      xlsx.writeFile(workbook, reportName + ".xlsx");

      this.generatedReport = [];
      this.isExportExcel = false;
    },

    async fetchContactRecords() {
      this.isLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query MasterContactPersons(
              $first: Int
              $page: Int
              $search: String
              $entities: [String]
              $partner_type: String
              $partner_ids: [Int]
            ) {
              masterContactPersons(
                first: $first
                page: $page
                search: $search
                entities: $entities
                partner_type: $partner_type
                partner_ids: $partner_ids
              ) {
                error
                error_code
                message
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
                data {
                  id
                  current_partner_id
                  name
                  phone
                  email
                  designation
                  entity
                  current_partner {
                    id
                    name
                    entity
                    is_pipeline
                  }
                  contact_person_entity {
                    entity
                  }
                }
              }
            }
          `,
          variables: Object.assign(
            {
              first: this.first,
              page: this.page,
            },
            this.buildListFilterVariables()
          ),
          fetchPolicy: "network-only",
        });

        const payload = data && data.masterContactPersons ? data.masterContactPersons : null;

        if (!payload || payload.error) {
          this.contactRecords = [];
          this.paginatorInfo = null;
          if (payload && payload.message) {
            this.showToast("error", payload.message);
          }
          return;
        }

        this.contactRecords = payload.data || [];
        this.paginatorInfo = payload.paginatorInfo;
      } catch (e) {
        console.error(e);
        this.contactRecords = [];
        this.paginatorInfo = null;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.mcl-filter-toggle-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.25rem;
  border-radius: 9999px;
  background: #2563eb;
  color: #ffffff;
  font-size: 0.625rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mcl-filter-hint {
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.mcl-active-filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #1e40af;
  background: #ffffff;
  border: 1px solid #bfdbfe;
}

.mcl-active-filter-clear {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: #2563eb;
}

.mcl-active-filter-clear:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.mcl-page {
  :deep(.multiselect) {
    min-height: 42px;
  }

  :deep(.multiselect__tags) {
    border: 1px solid #d1d5db;
    border-radius: 0.75rem;
    padding: 8px 40px 8px 12px;
    min-height: 42px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  :deep(.multiselect__tags-wrap) {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    vertical-align: middle;
  }

  :deep(.multiselect__input),
  :deep(.multiselect__single) {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 0;
    vertical-align: middle;
    align-self: center;
  }

  :deep(.multiselect__single) {
    padding-left: 0;
    margin-bottom: 0;
  }

  :deep(.multiselect__placeholder) {
    color: #9ca3af;
    margin-bottom: 0;
    padding-top: 0;
    line-height: 20px;
  }

  :deep(.multiselect__option--highlight) {
    background: #2563eb;
  }

  :deep(.multiselect__option--selected.multiselect__option--highlight) {
    background: #1d4ed8;
  }

  :deep(.multiselect__tag) {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  :deep(.multiselect__tag-icon:after) {
    color: #1d4ed8;
  }
}

.mcl-email-logs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(107, 114, 128, 0.35);
  background: linear-gradient(to bottom, #ffffff, rgba(107, 114, 128, 0.08));
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
  cursor: pointer;
}

.mcl-email-logs-btn:hover {
  border-color: rgba(107, 114, 128, 0.55);
  background: linear-gradient(to bottom, rgba(107, 114, 128, 0.08), rgba(107, 114, 128, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.mcl-email-logs-btn__icon {
  font-size: 1rem;
  color: #4b5563;
}

.mcl-email-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(15, 76, 129, 0.3);
  background: linear-gradient(to bottom, #ffffff, rgba(15, 76, 129, 0.08));
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f4c81;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
  cursor: pointer;
}

.mcl-email-btn:hover {
  border-color: rgba(15, 76, 129, 0.5);
  background: linear-gradient(to bottom, rgba(15, 76, 129, 0.08), rgba(15, 76, 129, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.mcl-email-btn__icon {
  font-size: 1rem;
  color: #0f4c81;
}

.mcl-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(37, 99, 235, 0.25);
  background: linear-gradient(to bottom, #ffffff, rgba(37, 99, 235, 0.08));
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e3a8a;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.mcl-add-btn:hover {
  border-color: rgba(37, 99, 235, 0.45);
  background: linear-gradient(to bottom, rgba(37, 99, 235, 0.08), rgba(37, 99, 235, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.mcl-add-btn__icon {
  width: 1rem;
  height: 1rem;
}

.mcl-export-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.25);
  background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #052e1f;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.mcl-export-btn:hover {
  border-color: rgba(16, 185, 129, 0.45);
  background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.mcl-export-btn:disabled {
  pointer-events: none;
  opacity: 0.6;
}

.mcl-export-btn__icon {
  font-size: 1.05rem;
  color: #059669;
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.option__small {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Add contact modal — matches Survey QR modal styling */
.mcl-modal-header {
  padding: 1.25rem 3rem 1.25rem 1.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%);
  color: #fff;
  border-radius: 1rem 1rem 0 0;
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.mcl-modal-header-inner {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.mcl-modal-header-icon-wrap {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.mcl-modal-header-icon {
  font-size: 1.35rem;
  opacity: 0.95;
}

.mcl-modal-header-title {
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.3;
}

.mcl-modal-header-sub {
  margin: 0.2rem 0 0 0;
  font-size: 0.8125rem;
  color: rgba(219, 234, 254, 0.95);
  line-height: 1.35;
}

.mcl-modal-body {
  padding: 1.5rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.mcl-modal-card {
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
  padding: 1.25rem;
  margin-bottom: 1rem;
}

.mcl-modal-card-partner {
  margin-bottom: 1.25rem;
}

.mcl-modal-grid {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 0.625rem;
  column-gap: 0.75rem;
}

@media (min-width: 480px) {
  .mcl-modal-grid {
    grid-template-columns: 1fr 1fr;
    row-gap: 0.5rem;
    column-gap: 0.625rem;
  }
}

.mcl-field {
  min-width: 0;
}

.mcl-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.25rem;
}

.mcl-label-block {
  margin-bottom: 0.5rem;
}

.mcl-required {
  color: #dc2626;
}

.mcl-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  color: #1e293b;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.625rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mcl-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.mcl-select {
  cursor: pointer;
}

.mcl-input-error {
  border-color: #f87171;
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.15);
}

.mcl-error-slot {
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
  transition: max-height 0.22s ease, margin-top 0.22s ease;
}

.mcl-error-slot--visible {
  max-height: 1.5rem;
  margin-top: 0.2rem;
}

.mcl-error {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: #b91c1c;
}

.mcl-error-fade-enter-active,
.mcl-error-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.mcl-error-fade-enter-from,
.mcl-error-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.mcl-error-fade-enter-to,
.mcl-error-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.mcl-partner-select-wrap {
  margin-top: 1rem;
}

.mcl-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-bottom: 0;
}

@media (min-width: 400px) {
  .mcl-modal-actions {
    flex-direction: row;
    gap: 0.75rem;
  }
}

.mcl-modal-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.625rem;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.mcl-modal-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

.mcl-modal-btn-primary {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
}

.mcl-modal-btn-primary:not(:disabled):hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.45);
  transform: translateY(-1px);
}

.mcl-modal-btn-secondary {
  background: #fff;
  color: #334155;
  border: 2px solid #e2e8f0;
}

.mcl-modal-btn-secondary:not(:disabled):hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.mcl-modal-hint {
  margin: 1.25rem 0 0 0;
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 0.5rem;
  border: 1px dashed #cbd5e1;
}

.mcl-modal-hint-icon {
  margin-right: 0.25rem;
  color: #f59e0b;
}

/* Dashboard statistics */
.mcl-stats {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.mcl-stats-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.mcl-stats-section__head {
  margin-bottom: 1rem;
}

.mcl-stats-section__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.mcl-stats-section__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.mcl-stats-overview {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.875rem;
}

@media (min-width: 640px) {
  .mcl-stats-overview {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .mcl-stats-overview {
    grid-template-columns: 1.35fr repeat(3, 1fr);
  }
}

.mcl-stats-entities {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

@media (min-width: 640px) {
  .mcl-stats-entities {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .mcl-stats-entities {
    grid-template-columns: repeat(5, 1fr);
  }
}

.mcl-stat-card {
  position: relative;
  overflow: hidden;
  border-radius: 0.875rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.mcl-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  border-color: #dbeafe;
}

.mcl-stat-card--total {
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 55%, #eff6ff 100%);
  border-color: #bfdbfe;
}

.mcl-stat-card__glow {
  position: absolute;
  top: -2rem;
  right: -2rem;
  width: 6rem;
  height: 6rem;
  border-radius: 9999px;
  opacity: 0.45;
  pointer-events: none;
}

.mcl-stat-card__glow--blue {
  background: radial-gradient(circle, rgba(37, 99, 235, 0.28) 0%, transparent 70%);
}

.mcl-stat-card__glow--current {
  background: radial-gradient(circle, rgba(16, 185, 129, 0.28) 0%, transparent 70%);
}

.mcl-stat-card__glow--pipeline {
  background: radial-gradient(circle, rgba(245, 158, 11, 0.28) 0%, transparent 70%);
}

.mcl-stat-card__glow--other {
  background: radial-gradient(circle, rgba(99, 102, 241, 0.28) 0%, transparent 70%);
}

.mcl-stat-card__body {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.125rem;
}

.mcl-stat-card__body--entity {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  padding: 1rem;
}

.mcl-stat-card__icon-wrap {
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
}

.mcl-stat-card__icon-wrap--blue {
  background: #dbeafe;
  color: #1d4ed8;
}

.mcl-stat-card__icon-wrap--current {
  background: #d1fae5;
  color: #047857;
}

.mcl-stat-card__icon-wrap--pipeline {
  background: #fef3c7;
  color: #b45309;
}

.mcl-stat-card__icon-wrap--other {
  background: #e0e7ff;
  color: #4338ca;
}

.mcl-stat-card__content {
  min-width: 0;
}

.mcl-stat-card__label {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.mcl-stat-card__value {
  margin: 0.2rem 0 0;
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.1;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.mcl-stat-card__value--sm {
  font-size: 1.5rem;
}

.mcl-stat-card__value--loading {
  width: 3.5rem;
  height: 1.75rem;
  border-radius: 0.375rem;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: mcl-stat-shimmer 1.2s ease-in-out infinite;
}

.mcl-stat-card__entity-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.mcl-stat-card__entity-caption {
  margin: 0;
  font-size: 0.75rem;
  color: #94a3b8;
}

.mcl-stat-card--entity-iat .mcl-stat-card__entity-badge {
  background: #eff6ff;
  color: #1d4ed8;
}

.mcl-stat-card--entity-ats .mcl-stat-card__entity-badge {
  background: #ecfdf5;
  color: #047857;
}

.mcl-stat-card--entity-fchs .mcl-stat-card__entity-badge {
  background: #fef3c7;
  color: #b45309;
}

.mcl-stat-card--entity-adpoly .mcl-stat-card__entity-badge {
  background: #f3e8ff;
  color: #7e22ce;
}

.mcl-stat-card--entity-adveti .mcl-stat-card__entity-badge {
  background: #ffe4e6;
  color: #be123c;
}

@keyframes mcl-stat-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}
</style>
